import { useCallback, useEffect, useMemo, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { ChevronDown, MapPin, Plus, Search, X } from 'lucide-react'

import { useCenterStore } from '@caradasstore/CenterStore'
import { useLoadingStore } from '@caradasstore/LoadingStore'
import { useUserStore } from '@caradasstore/UserStore'
import { useMobileToolbarStore } from '@caradasstore/MobileToolbarStore'
import { getSupportTickets, getSupportTicketsConstants, getSupportTicketStatusCount } from '../../../api/api'
import { Logger } from '../../../logger'
import { scrollPageTop } from '../../../utils/useScrollTop'
import { SupportRequestEnums } from 'enums/enums'
import { CreateSupportRequestModal } from '../support-request-modal'

const formatShortDate = (value) => {
  if (!value) return ''
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleDateString('en-US', { month: 'short', day: '2-digit' })
}

const STATUS_TABS = [
  { key: 'ALL', label: 'All' },
  { key: 'NEW', label: 'New' },
  { key: 'OPEN', label: 'Open' },
  { key: 'CLOSED', label: 'Closed' }
]

const STATUS_PILL = {
  NEW: { bg: 'bg-[#E8EFFF]', text: 'text-[#2C3D4D]', dot: 'bg-[#2C3D4D]', label: 'New' },
  OPEN: { bg: 'bg-[#FEF3C7]', text: 'text-[#D97706]', dot: 'bg-[#D97706]', label: 'Open' },
  CLOSED: { bg: 'bg-[#EBEDF0]', text: 'text-[#6B7280]', dot: 'bg-[#6B7280]', label: 'Closed' }
}

// Matches the Quote queue created-date format: "May 27, 2026 7:43 PM".
const formatDateTime = (value) => {
  if (!value) return ''
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return ''
  const date = d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
  return `${date} ${time}`
}

const StatusPill = ({ status }) => {
  const s = STATUS_PILL[String(status).toUpperCase()] || { bg: 'bg-[#EBEDF0]', text: 'text-[#6B7280]', dot: 'bg-[#6B7280]', label: status }
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full ${s.bg} px-2.5 py-1 text-[12px] font-bold ${s.text}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${s.dot}`} />
      {s.label}
    </span>
  )
}

export const SupportQueuePage = () => {
  const location = useLocation()
  const history = useHistory()
  const queryParams = new URLSearchParams(location.search)
  const statusFilter = queryParams.get('status') || 'NEW'

  const ccId = useCenterStore((store) => store.centerDetail?.id)
  const user = useUserStore((store) => store.currentUser)
  const setLoading = useLoadingStore((store) => store.setLoading)

  const [pageParams, setPageParams] = useState({
    pageNo: 0,
    totalPages: 0,
    totalElements: 0,
    pageSize: 50,
    status: statusFilter,
    search: ''
  })
  const [searchInput, setSearchInput] = useState('')
  const [sort, setSort] = useState('DESC')
  const [supportTickets, setSupportTickets] = useState([])
  const [supportTicketsStatusCounts, setSupportTicketsStatusCounts] = useState(null)
  const [supportRequestConstants, setSupportRequestConstants] = useState({})
  const [createOpen, setCreateOpen] = useState(false)
  const searchOpen = useMobileToolbarStore((s) => s.searchOpen)
  const closeMobileToolbar = useMobileToolbarStore((s) => s.closeAll)
  useEffect(() => () => closeMobileToolbar(), [closeMobileToolbar])

  useEffect(() => {
    if (!user?.is_admin) return
    const fetchSupportTicketCountData = async () => {
      try {
        const supportTicketCount = await getSupportTicketStatusCount({ ccId })
        setSupportTicketsStatusCounts(supportTicketCount?.data)
      } catch (error) {
        Logger.error({ message: error, payload: { file: 'SupportQueuePage', method: 'getSupportTicketStatusCount' } })
      }
    }
    fetchSupportTicketCountData()
  }, [ccId, user?.is_admin])

  useEffect(() => {
    const fetchConstants = async () => {
      try {
        const constants = await getSupportTicketsConstants()
        setSupportRequestConstants(constants?.data || {})
      } catch (error) {
        Logger.error({ message: error, payload: { file: 'SupportQueuePage', method: 'getSupportTicketsConstants' } })
      }
    }
    fetchConstants()
  }, [])

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setPageParams((prev) => ({ ...prev, search: searchInput, pageNo: 0 }))
    }, 500)
    return () => clearTimeout(timeoutId)
  }, [searchInput])

  useEffect(() => {
    const fetchSupportTickets = async () => {
      setLoading(true)
      try {
        const response = await getSupportTickets({
          pageNo: pageParams.pageNo,
          pageSize: pageParams.pageSize,
          status: pageParams.status === 'ALL' ? 'OPEN,CLOSED,NEW' : pageParams.status,
          ccid: ccId,
          search: pageParams.search,
          sort
        })
        if (response?.status === 200) {
          setSupportTickets(response.data.supportTickets || [])
          setPageParams((prev) => ({
            ...prev,
            totalPages: response.data.totalPages || 0,
            totalElements: response.data.totalElements ?? (response.data.supportTickets?.length || 0)
          }))
          scrollPageTop()
        }
      } catch (error) {
        Logger.error({ message: error, payload: { pageParams, ccId } })
      } finally {
        setLoading(false)
      }
    }
    fetchSupportTickets()
  }, [ccId, pageParams.pageNo, pageParams.pageSize, pageParams.status, pageParams.search, sort, setLoading])

  // Map raw tickets → display rows (resolves type / problem-area labels from constants).
  const supportTicketsQueueData = useMemo(
    () =>
      supportTickets.map((ticket) => ({
        ...ticket,
        assignedTo: ticket.assigned_to?.name || '',
        calibration_name: ticket.calibrationCenter?.name || '',
        contact_name: ticket.contact_user_id?.name || '',
        support_type: supportRequestConstants?.supportTypes?.find((t) => t.slug === ticket.type)?.type || '',
        problem_area:
          SupportRequestEnums.CALIBRATION_DIAGNOSTIC === ticket.type
            ? supportRequestConstants?.supportProblemAreas?.find((a) => a.slug === ticket.problem_area)?.problemArea || ''
            : supportRequestConstants?.supportItRequestTypes?.find((t) => t.slug === ticket.it_request_type)?.itRequestType || ''
      })),
    [supportTickets, supportRequestConstants]
  )

  const onStatusFilterSelected = useCallback(
    (status) => {
      history.replace({ pathname: window.location.pathname, search: `?status=${status}` })
      setPageParams((prev) => ({ ...prev, status, pageNo: 0 }))
    },
    [history]
  )

  const onRowClick = (ticket) => history.push(`/support/support-request/${ticket.id}`)

  // Counts (admin only; non-admin sees em-dashes).
  const counts = {
    NEW: supportTicketsStatusCounts?.newSupportTicketsCounts,
    OPEN: supportTicketsStatusCounts?.openSupportTicketsCounts,
    CLOSED: supportTicketsStatusCounts?.closedSupportTicketsCounts
  }
  const totalCount = supportTicketsStatusCounts?.totalSupportTicketsCounts ?? (counts.NEW || 0) + (counts.OPEN || 0) + (counts.CLOSED || 0)
  const displayCount = (key) => (key === 'ALL' ? totalCount : counts[key])

  const startRow = pageParams.totalElements === 0 ? 0 : pageParams.pageNo * pageParams.pageSize + 1
  const endRow = Math.min((pageParams.pageNo + 1) * pageParams.pageSize, pageParams.totalElements)

  return (
    <div className='mx-auto max-w-[1400px] px-3 py-6 min-[435px]:px-0'>
      {/* Header (desktop only — mobile uses the global toolbar title) */}
      <div className='hidden min-[435px]:flex mb-5 flex-wrap items-end justify-between gap-3'>
        <div>
          <div className='text-[11px] font-bold uppercase tracking-[0.16em] text-[#6B7280]'>Help Center</div>
          <h1 className='mt-1 text-[28px] font-bold leading-tight tracking-tight text-[#0A1628]'>Support Requests</h1>
        </div>
        <button
          type='button'
          onClick={() => setCreateOpen(true)}
          className='inline-flex items-center gap-2 rounded-lg bg-sidebar px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-sidebar/90'
        >
          <Plus className='h-4 w-4' />
          New Support Request
        </button>
      </div>

      {/* Mobile inline search */}
      {searchOpen && (
        <div className='min-[435px]:hidden mb-3 flex items-center gap-2 rounded-lg bg-[#F4F6F9] px-3 py-2.5'>
          <Search className='h-4 w-4 text-[#6B7280]' />
          <input
            type='text'
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            autoFocus
            placeholder='Search by #, assignee or center'
            className='flex-1 border-0 bg-transparent text-[14px] text-[#0A1628] placeholder:text-[#6B7280] focus:outline-none'
          />
          {searchInput && (
            <button
              onClick={() => setSearchInput('')}
              aria-label='Clear search'
              className='inline-flex h-6 w-6 items-center justify-center rounded-full bg-white text-[#475569]'
            >
              <X className='h-3 w-3' />
            </button>
          )}
        </div>
      )}

      {/* Mobile underline tabs */}
      <div className='min-[435px]:hidden mb-4 flex items-center gap-6 overflow-x-auto border-b border-[#EBEDF0]'>
        {STATUS_TABS.map((tab) => {
          const active = pageParams.status === tab.key
          const c = displayCount(tab.key)
          return (
            <button
              key={tab.key}
              type='button'
              onClick={() => onStatusFilterSelected(tab.key)}
              className={`relative inline-flex shrink-0 items-center gap-1.5 pb-2.5 text-[14px] font-semibold transition-colors ${
                active ? 'text-[#0A1628] border-b-2 border-[#1F6FEB] -mb-px' : 'text-[#6B7280]'
              }`}
            >
              {tab.label}
              {c != null && (
                <span
                  className={`inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-[11px] font-bold ${
                    active ? 'bg-[#E8EFFF] text-[#1F6FEB]' : 'bg-[#EBEDF0] text-[#6B7280]'
                  }`}
                >
                  {c}
                </span>
              )}
            </button>
          )
        })}
      </div>

      {/* Desktop filter row */}
      <div className='hidden min-[435px]:flex mb-4 flex-wrap items-center gap-3'>
        <div className='flex flex-wrap items-center gap-2'>
          {STATUS_TABS.map((tab) => {
            const active = pageParams.status === tab.key
            const c = displayCount(tab.key)
            return (
              <button
                key={tab.key}
                type='button'
                onClick={() => onStatusFilterSelected(tab.key)}
                className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[13px] font-semibold transition-colors ${
                  active ? 'bg-[#0A1628] text-white' : 'bg-white text-[#6B7280] hover:text-[#0A1628]'
                }`}
              >
                {tab.label}
                {c != null && (
                  <span
                    className={`inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-[11px] font-bold ${
                      active ? 'bg-white/15 text-white' : 'bg-[#EBEDF0] text-[#6B7280]'
                    }`}
                  >
                    {c}
                  </span>
                )}
              </button>
            )
          })}
        </div>

        <div className='relative ml-auto min-w-[280px]'>
          <Search className='pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#6B7280]' />
          <input
            type='text'
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder='Search by #, assignee or center...'
            className='h-10 w-full rounded-lg border border-[#D1D5D8] bg-white pl-9 pr-3 text-[13px] text-[#0A1628] placeholder-[#9CA3AF] focus:border-[#1F6FEB] focus:outline-none'
          />
        </div>
      </div>

      {/* Mobile card list */}
      <div className='min-[435px]:hidden space-y-3 pb-24'>
        {supportTicketsQueueData.length === 0 ? (
          <div className='rounded-xl border border-[#D1D5D8] bg-white px-4 py-10 text-center text-[14px] text-[#6B7280]'>
            No support requests found.
          </div>
        ) : (
          supportTicketsQueueData.map((row) => {
            const statusKey = String(row.supportTicketStatus || row.status || '').toUpperCase()
            const pill = STATUS_PILL[statusKey] || { bg: 'bg-[#EBEDF0]', text: 'text-[#6B7280]', dot: 'bg-[#6B7280]', label: statusKey }
            return (
              <button
                key={row.id}
                type='button'
                onClick={() => onRowClick(row)}
                className='w-full rounded-xl border border-[#D1D5D8] bg-white p-4 text-left transition-colors hover:bg-[#F8FAFC]'
              >
                <div className='flex items-start justify-between gap-3'>
                  <div className='min-w-0 flex-1'>
                    <div className='text-[13px] font-semibold text-[#1F6FEB]'>#{row.ticket_number ?? row.supportTicketId ?? row.id}</div>
                    <div className='mt-1.5 truncate text-[16px] font-bold leading-tight text-[#0A1628]'>{row.support_type || '—'}</div>
                    {row.problem_area && <div className='mt-0.5 text-[13px] text-[#6B7280]'>{row.problem_area}</div>}
                  </div>
                  <span
                    className={`inline-flex shrink-0 items-center gap-1.5 rounded-full ${pill.bg} px-2.5 py-1 text-[12px] font-bold ${pill.text}`}
                  >
                    <span className={`h-1.5 w-1.5 rounded-full ${pill.dot}`} />
                    {pill.label}
                  </span>
                </div>
                {(row.calibration_name || row.assignedTo || row.created_at) && (
                  <div className='mt-3 flex items-center justify-between gap-3 border-t border-[#EBEDF0] pt-3 text-[13px]'>
                    <div className='flex min-w-0 items-center gap-1.5 text-[#2C3D4D]'>
                      <MapPin className='h-3.5 w-3.5 shrink-0 text-[#9CA3AF]' />
                      <span className='truncate'>{row.calibration_name || '—'}</span>
                    </div>
                    <div className='shrink-0 text-[12px] text-[#6B7280]'>
                      {row.assignedTo || 'Unassigned'}
                      {row.created_at && (
                        <>
                          <span className='mx-1.5 text-[#CBD5E1]'>·</span>
                          {formatShortDate(row.created_at)}
                        </>
                      )}
                    </div>
                  </div>
                )}
              </button>
            )
          })
        )}
      </div>

      {/* Floating New Request button (mobile only) */}
      <button
        type='button'
        onClick={() => setCreateOpen(true)}
        className='min-[435px]:hidden fixed bottom-6 right-4 z-30 inline-flex items-center gap-2 rounded-full bg-[#0A1628] px-6 py-3.5 text-[14px] font-bold text-white shadow-lg transition-colors hover:bg-[#1F2D3D]'
      >
        <Plus className='h-5 w-5' />
        New Request
      </button>

      {/* Desktop table */}
      <div className='hidden min-[435px]:block overflow-hidden rounded-xl border border-[#EBEDF0] bg-white'>
        <div className='overflow-x-auto'>
          <table className='w-full text-left text-[13px]'>
            <thead>
              <tr className='bg-gradient-to-r from-[#2C3D4D] to-[#1F2D3D] text-white'>
                <th className='px-4 py-3 text-[11px] font-bold uppercase tracking-wide'>Date Created</th>
                <th className='px-4 py-3 text-[11px] font-bold uppercase tracking-wide'>Support #</th>
                <th className='px-4 py-3 text-[11px] font-bold uppercase tracking-wide'>Assigned To</th>
                <th className='px-4 py-3 text-[11px] font-bold uppercase tracking-wide'>Support Type</th>
                <th className='px-4 py-3 text-[11px] font-bold uppercase tracking-wide'>Problem Area</th>
                {user?.is_admin && <th className='px-4 py-3 text-[11px] font-bold uppercase tracking-wide'>CC Name</th>}
                <th className='px-4 py-3 text-[11px] font-bold uppercase tracking-wide'>Status</th>
              </tr>
            </thead>
            <tbody>
              {supportTicketsQueueData.length === 0 ? (
                <tr>
                  <td colSpan={user?.is_admin ? 7 : 6} className='px-4 py-10 text-center text-[#6B7280]'>
                    No support requests found.
                  </td>
                </tr>
              ) : (
                supportTicketsQueueData.map((row) => (
                  <tr
                    key={row.id}
                    onClick={() => onRowClick(row)}
                    className='cursor-pointer border-t border-[#EBEDF0] transition-colors even:bg-[#F8FAFC] hover:bg-[#F1F4F8]'
                  >
                    <td className='px-4 py-3 font-mono text-[12px] text-[#475569]'>{formatDateTime(row.created_at)}</td>
                    <td className='whitespace-nowrap px-4 py-3'>
                      <span className='inline-flex items-center whitespace-nowrap rounded-md bg-[#E8EFFF] px-2 py-0.5 text-[12px] font-semibold text-[#2C3D4D]'>
                        #{row.ticket_number ?? row.supportTicketId ?? row.id}
                      </span>
                    </td>
                    <td className='px-4 py-3 text-[#0A1628]'>{row.assignedTo || <span className='text-[#9CA3AF]'>— Unassigned</span>}</td>
                    <td className='px-4 py-3 text-[#0A1628]'>{row.support_type}</td>
                    <td className='px-4 py-3 text-[#0A1628]'>{row.problem_area}</td>
                    {user?.is_admin && <td className='px-4 py-3 text-[#0A1628]'>{row.calibration_name}</td>}
                    <td className='px-4 py-3'>
                      <StatusPill status={row.supportTicketStatus || row.status} />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {supportTicketsQueueData.length > 0 && (
          <div className='flex flex-wrap items-center justify-between gap-3 border-t border-[#EBEDF0] px-4 py-3 text-[13px] text-[#6B7280]'>
            <div>
              Showing {endRow === 0 ? 0 : startRow}–{endRow} of {pageParams.totalElements}
            </div>
            <div className='flex items-center gap-3'>
              <div className='flex items-center gap-2'>
                <button
                  type='button'
                  disabled={pageParams.pageNo <= 0}
                  onClick={() => setPageParams((p) => ({ ...p, pageNo: Math.max(0, p.pageNo - 1) }))}
                  className='inline-flex h-8 items-center rounded-md border border-[#D1D5D8] bg-white px-3 font-medium text-[#0A1628] hover:bg-[#F8FAFC] disabled:cursor-not-allowed disabled:opacity-50'
                >
                  Prev
                </button>
                <button
                  type='button'
                  disabled={pageParams.pageNo + 1 >= pageParams.totalPages}
                  onClick={() => setPageParams((p) => ({ ...p, pageNo: Math.min(p.totalPages - 1, p.pageNo + 1) }))}
                  className='inline-flex h-8 items-center rounded-md border border-[#D1D5D8] bg-white px-3 font-medium text-[#0A1628] hover:bg-[#F8FAFC] disabled:cursor-not-allowed disabled:opacity-50'
                >
                  Next
                </button>
              </div>
              <div className='relative'>
                <select
                  value={pageParams.pageSize}
                  onChange={(e) => setPageParams((p) => ({ ...p, pageSize: Number(e.target.value), pageNo: 0 }))}
                  className='h-8 appearance-none rounded-md border border-[#D1D5D8] bg-white pl-3 pr-8 text-[13px] text-[#0A1628] focus:border-[#1F6FEB] focus:outline-none'
                >
                  {[25, 50, 100, 200].map((n) => (
                    <option key={n} value={n}>
                      {n} per page
                    </option>
                  ))}
                </select>
                <ChevronDown className='pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#6B7280]' />
              </div>
            </div>
          </div>
        )}
      </div>

      <CreateSupportRequestModal open={createOpen} onClose={() => setCreateOpen(false)} />
    </div>
  )
}
